import { FC, FormEvent, useState, MouseEvent, ReactNode } from 'react';

import { Button, Dialog } from '@robocorp/components';
import { useTranslation } from 'react-i18next';

export type ConfirmCallback = (
  callback: (event: MouseEvent) => void,
) => (event: MouseEvent) => void;

export type ConfirmProps = {
  title: string;
  text: ReactNode;
  children: (callback: ConfirmCallback) => ReactNode;
  confirmButtonText?: string;
};

const Confirm: FC<ConfirmProps> = ({ children, title, text, confirmButtonText }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [callback, setCallback] = useState<(() => void) | null>(null);

  const { t } = useTranslation();

  const onOpen = (cb: (event: MouseEvent) => void) => (event: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    setOpen(true);
    setCallback(() => () => cb(event));
  };

  const onClose = (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(false);
  };

  const onConfirm = (e: FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (callback) {
      callback();
    }

    onClose(e);
  };

  return (
    <>
      {children(onOpen)}
      <Dialog open={open} onClose={onClose}>
        <Dialog.Header>
          <Dialog.Header.Title title={title} />
          <Dialog.Header.Description>{text}</Dialog.Header.Description>
        </Dialog.Header>
        <Dialog.Actions>
          <Button onClick={onConfirm} data-testid="dialog__confirm-submit" className="primary">
            {confirmButtonText || t('confirm')}
          </Button>
          <Button onClick={onClose} variant="secondary">
            {t('cancel')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default Confirm;
